import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import {
  usePlannedMaintenanceList,
  useCreateMaintenance,
  useUpdateMaintenance,
  useDeleteMaintenance,
} from "../hooks/useMaintenanceStatus";
import type { PlannedMaintenance, PlannedMaintenanceFormData } from "../types";

const MAX_PAST_MS = 5 * 60 * 1000;
const MAX_DURATION_MS = 4 * 60 * 60 * 1000;

const schema = z
  .object({
    title: z.string().min(1, "Tytuł jest wymagany").max(200),
    message: z.string().min(1, "Opis jest wymagany"),
    starts_at: z.string().min(1, "Data rozpoczęcia jest wymagana"),
    ends_at: z.string().min(1, "Data zakończenia jest wymagana"),
    is_enabled: z.boolean(),
  })
  .refine(
    (d) => d.starts_at === "" || new Date(d.starts_at).getTime() >= Date.now() - MAX_PAST_MS,
    { message: "Data rozpoczęcia nie może być wcześniejsza niż 5 minut w przeszłości", path: ["starts_at"] }
  )
  .refine(
    (d) => d.ends_at === "" || d.starts_at === "" || new Date(d.ends_at) > new Date(d.starts_at),
    { message: "Data zakończenia musi być późniejsza niż rozpoczęcia", path: ["ends_at"] }
  )
  .refine(
    (d) =>
      d.ends_at === "" ||
      d.starts_at === "" ||
      new Date(d.ends_at).getTime() - new Date(d.starts_at).getTime() <= MAX_DURATION_MS,
    { message: "Maksymalny czas trwania wdrożenia to 4 godziny", path: ["ends_at"] }
  );

type FormValues = z.infer<typeof schema>;

function toLocalDatetimeValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDateRange(starts: string, ends: string): string {
  const s = new Date(starts);
  const e = new Date(ends);
  const opts: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" };
  return `${s.toLocaleString("pl-PL", opts)} – ${e.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}`;
}

function getStatus(w: PlannedMaintenance): { label: string; cls: string } {
  if (!w.is_enabled) return { label: "Wyłączone", cls: "bg-gray-100 text-gray-600" };
  const now = Date.now();
  const start = new Date(w.starts_at).getTime();
  const end = new Date(w.ends_at).getTime();
  if (now >= start && now <= end) return { label: "Aktywne", cls: "bg-red-100 text-red-700 font-semibold" };
  if (now < start) return { label: "Nadchodzące", cls: "bg-amber-100 text-amber-700" };
  return { label: "Zakończone", cls: "bg-green-100 text-green-700" };
}

interface ModalProps {
  editing: PlannedMaintenance | null;
  onClose: () => void;
}

function MaintenanceModal({ editing, onClose }: ModalProps) {
  const createMutation = useCreateMaintenance();
  const updateMutation = useUpdateMaintenance(editing?.id ?? "");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: editing
      ? {
          title: editing.title,
          message: editing.message,
          starts_at: toLocalDatetimeValue(editing.starts_at),
          ends_at: toLocalDatetimeValue(editing.ends_at),
          is_enabled: editing.is_enabled,
        }
      : {
          title: "",
          message: "",
          starts_at: toLocalDatetimeValue(new Date(Date.now() + 15 * 60_000).toISOString()),
          ends_at: toLocalDatetimeValue(new Date(Date.now() + 45 * 60_000).toISOString()),
          is_enabled: true,
        },
  });

  const onSubmit = async (values: FormValues) => {
    const payload: PlannedMaintenanceFormData = {
      ...values,
      starts_at: new Date(values.starts_at).toISOString(),
      ends_at: new Date(values.ends_at).toISOString(),
    };
    if (editing) {
      await updateMutation.mutateAsync(payload);
    } else {
      await createMutation.mutateAsync(payload);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--color-border)]">
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {editing ? "Edytuj przerwę techniczną" : "Nowa przerwa techniczna"}
          </h2>
          <button onClick={onClose} className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-mono text-[var(--color-text-muted)] mb-1">Tytuł</label>
            <input
              {...register("title")}
              className="w-full border border-[var(--color-border)] rounded-lg px-3 py-2 text-sm bg-[var(--color-bg-base)] text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
              placeholder="np. Wdrożenie v2.5"
            />
            {errors.title && <p className="text-xs text-red-600 mt-1">{errors.title.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-mono text-[var(--color-text-muted)] mb-1">Komunikat dla użytkowników</label>
            <textarea
              {...register("message")}
              rows={3}
              className="w-full border border-[var(--color-border)] rounded-lg px-3 py-2 text-sm bg-[var(--color-bg-base)] text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] resize-none"
              placeholder="np. Aktualizacja systemu, wdrażamy nowe funkcje..."
            />
            {errors.message && <p className="text-xs text-red-600 mt-1">{errors.message.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono text-[var(--color-text-muted)] mb-1">Początek</label>
              <input
                type="datetime-local"
                {...register("starts_at")}
                className="w-full border border-[var(--color-border)] rounded-lg px-3 py-2 text-sm bg-[var(--color-bg-base)] text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
              />
              {errors.starts_at && <p className="text-xs text-red-600 mt-1">{errors.starts_at.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-mono text-[var(--color-text-muted)] mb-1">Koniec</label>
              <input
                type="datetime-local"
                {...register("ends_at")}
                className="w-full border border-[var(--color-border)] rounded-lg px-3 py-2 text-sm bg-[var(--color-bg-base)] text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
              />
              {errors.ends_at && <p className="text-xs text-red-600 mt-1">{errors.ends_at.message}</p>}
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" {...register("is_enabled")} className="rounded" />
            <span className="text-sm text-[var(--color-text-secondary)]">Włączone</span>
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-mono border border-[var(--color-border)] rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] transition-colors"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-xs font-mono bg-[var(--color-primary)] text-[var(--color-text-inverse)] rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {isSubmitting ? "Zapisuję..." : editing ? "Zapisz zmiany" : "Utwórz"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function MaintenanceManagePage() {
  const user = useAuthStore((s) => s.user);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PlannedMaintenance | null>(null);
  const { data: windows = [], isLoading } = usePlannedMaintenanceList();
  const deleteMutation = useDeleteMaintenance();

  if (!user?.is_developer && user?.role !== "director") return <Navigate to="/" replace />;

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (w: PlannedMaintenance) => { setEditing(w); setModalOpen(true); };
  const handleDelete = async (id: string) => {
    if (!confirm("Usunąć tę przerwę techniczną?")) return;
    await deleteMutation.mutateAsync(id);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Przerwy techniczne</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-1" />
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
            Zaplanowane wdrożenia i wyłączenia serwisu
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono bg-[var(--color-primary)] text-[var(--color-text-inverse)] rounded-lg hover:opacity-90 transition-opacity"
        >
          <Plus size={13} />
          Nowa przerwa
        </button>
      </div>

      <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-xs text-[var(--color-text-muted)]">Ładowanie...</div>
        ) : windows.length === 0 ? (
          <div className="p-8 text-center text-xs text-[var(--color-text-muted)]">
            Brak zaplanowanych przerw technicznych.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-left">
                <th className="px-4 py-2 font-mono text-[var(--color-text-muted)]">Tytuł</th>
                <th className="px-4 py-2 font-mono text-[var(--color-text-muted)] hidden md:table-cell">Termin</th>
                <th className="px-4 py-2 font-mono text-[var(--color-text-muted)]">Status</th>
                <th className="px-4 py-2 font-mono text-[var(--color-text-muted)] hidden lg:table-cell">Autor</th>
                <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />
              </tr>
            </thead>
            <tbody>
              {windows.map((w) => {
                const { label, cls } = getStatus(w);
                const endsAt = new Date(w.ends_at).getTime();
                const isCompleted = endsAt <= Date.now();
                const isEditLocked = endsAt <= Date.now() - 60 * 60 * 1000;
                return (
                  <tr key={w.id} className="border-b border-[var(--color-border-muted)] last:border-0 hover:bg-[var(--color-bg-elevated)] transition-colors">
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-[var(--color-text-primary)]">{w.title}</p>
                      <p className="text-[var(--color-text-muted)] truncate max-w-xs">{w.message}</p>
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell text-[var(--color-text-secondary)] whitespace-nowrap">
                      {formatDateRange(w.starts_at, w.ends_at)}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-mono ${cls}`}>
                        {label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 hidden lg:table-cell text-[var(--color-text-muted)]">
                      {w.created_by_name ?? "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1 justify-end">
                        {!isEditLocked && (
                          <button
                            onClick={() => openEdit(w)}
                            className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
                            title="Edytuj"
                          >
                            <Pencil size={13} />
                          </button>
                        )}
                        {!isCompleted && (
                          <button
                            onClick={() => handleDelete(w.id)}
                            className="p-1 text-[var(--color-text-muted)] hover:text-red-600 transition-colors"
                            title="Usuń"
                            data-testid={`delete-${w.id}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <MaintenanceModal editing={editing} onClose={() => setModalOpen(false)} />
      )}
    </div>
  );
}
